'use client';

import { CustomImage as Image } from '..';
import { useCallback, useState } from 'react';
import { useCartStore, useShallow, useModal } from '..';
import { DotSpinner } from 'components/shared';
import { SupplementProduct } from 'content/products/supplements';
import { CurrencySymbol, useFormatPrice } from 'hooks/pricing/useFormattedPrice';
import { Currency } from 'utils/Currency';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { ProductType } from 'components/shared/Cart/types';
import SupplementLearnMoreModal from './SupplementLearnMoreModal';
import { PlusCircleDark } from 'assets/icons/PlusCircleDark';
import { useTranslations } from 'next-intl';
import {
  ShopProductCardRoot,
  ShopCardBody,
  ShopCardFooterRow,
  ShopCardPlanTileMedia,
  ShopCardPriceCluster,
  ShopCardPrimaryCta,
  ShopCardSpecSection,
  ShopCardTag,
  ShopCardTagText,
} from './shopProductCard';
import styles from './ProductCardSupplements.module.css';

export const ProductCardSupplements = ({
  className,
  product,
  originalPrice,
  discountPercentage,
}: {
    className?: string;
    product: SupplementProduct;
    originalPrice?: string;
    discountPercentage?: string;
  }) => {
    const ModalClient = useModal();
    const CartClient = useCartStore(
      useShallow((s) => ({
        store: s.store,
        addToCart: s.addToCart,
        currency: s.currency,
        taxString: s.taxString,
      })),
    );
    const [isLoading, setIsLoading] = useState(false);
    const tCart = useTranslations('cart');

    const openLearnMore = useCallback(() => {
      ModalClient.set(
        <SupplementLearnMoreModal
          product={product}
          onClose={() => ModalClient.clear()}
        />,
      );
      ModalClient.setFullView();
      ModalClient.setCloseBtnHidden(true);
      ModalClient.setTop();
      ModalClient.setAlignRight();
      ModalClient.show();
    }, [ModalClient, product]);
    const formatPriceWithComma = useFormatPrice();

    const isSoldOut = product.tag === 'sold out';

    const handleAddToCart = async () => {
      const variantId = product.data?.[CartClient.store as ShopifyStore]
        ?.shopifyVariantId as string;
      if (!variantId) return;

      try {
        setIsLoading(true);
        await CartClient.addToCart([
          {
            shopifyVariantId: variantId,
            productType: ProductType.SUPPLEMENT,
          },
        ]);
      } finally {
        setIsLoading(false);
      }
    };

    const price =
      (product.data?.[CartClient.store as ShopifyStore]?.price as number) ??
      product.price;

    return (
      <ShopProductCardRoot
        className={[styles.root, className].filter(Boolean).join(' ')}
      >
        <ShopCardPlanTileMedia onClick={openLearnMore}>
          <Image
            src={product.imgSrc}
            alt={product.title}
            height={335}
            width={355}
            style={{
              objectFit: 'cover',
              width: '100%',
              borderRadius: '12px',
            }}
          />
        </ShopCardPlanTileMedia>
        <ShopCardBody>
          <ShopCardSpecSection className={`specBlock ${styles.specBlock}`}>
            <h3 className="title">{product.title}</h3>
            <p className="description">
              {product.formSize && (
                <span className="formSize">{product.formSize} | </span>
              )}
              {product.description}{' '}
              <button
                className="learnMoreLink"
                onClick={openLearnMore}
                data-buttontype="learn more"
                data-product={product.title}
              >
                {tCart('sharedCart.upsell.learnMore')}{' '}
                <PlusCircleDark
                  fill="#007FF5"
                  style={{
                    width: '12px',
                    height: '12px',
                    display: 'inline-block',
                    verticalAlign: 'middle',
                    marginLeft: '2px',
                  }}
                />
              </button>
            </p>
            <br />
          </ShopCardSpecSection>
          <ShopCardFooterRow>
            <ShopCardPriceCluster className="pricing-info">
              {originalPrice && discountPercentage && (
                <span className="striked">
                  {
                    CurrencySymbol[
                      (CartClient.currency ?? Currency.usd) as Currency
                    ]
                  }
                  {formatPriceWithComma(parseFloat(originalPrice))}
                </span>
              )}
              {
                CurrencySymbol[
                  (CartClient.currency ?? Currency.usd) as Currency
                ]
              }
              {formatPriceWithComma(
                originalPrice && discountPercentage
                  ? price * (1 - parseFloat(discountPercentage) / 100)
                  : price,
              )}
              <span> {CartClient.taxString} </span>
            </ShopCardPriceCluster>
            <ShopCardPrimaryCta
              className={
                isSoldOut || !product.data || isLoading
                  ? 'quickAdd disable'
                  : 'quickAdd'
              }
              onClick={handleAddToCart}
              data-buttontype="add to cart"
              data-product={product.title}
              disabled={isSoldOut || !product.data || isLoading}
              aria-busy={isLoading}
            >
              {isLoading ? (
                <DotSpinner color="#FFFFFF" />
              ) : (
                tCart('sharedCart.upsell.addToCart')
              )}
            </ShopCardPrimaryCta>
          </ShopCardFooterRow>
        </ShopCardBody>
        {product.tag ? <ShopCardTag><ShopCardTagText tag={product.tag} /></ShopCardTag> : null}
      </ShopProductCardRoot>
    );
};
